// src/pages/Blogs.jsx
import React, { useState } from "react";
import Navbar from "../components/Navbar";

const blogPosts = [
  {
    id: 1,
    title: "Top 5 Web Development Trends in 2025",
    description:
      "Explore the most important technologies and approaches shaping the future of websites.",
    date: "June 20, 2025",
    image: "/assets/img/blog1.jpg",
    category: "Web Development",
    readTime: "5 min read",
    author: "John Doe",
    tags: ["React", "JavaScript", "Web3", "AI"],
  },
  {
    id: 2,
    title: "Why Your Business Needs a Mobile App",
    description:
      "Discover how mobile apps drive engagement and revenue growth in a competitive market.",
    date: "June 10, 2025",
    image: "/assets/img/blog2.jpg",
    category: "Mobile Development",
    readTime: "7 min read",
    author: "Jane Smith",
    tags: ["Mobile", "Business", "Strategy", "UX"],
  },
  {
    id: 3,
    title: "SEO Tips to Improve Website Ranking",
    description:
      "Practical strategies to boost your site's visibility and climb Google rankings.",
    date: "May 28, 2025",
    image: "/assets/img/blog3.jpg",
    category: "SEO",
    readTime: "6 min read",
    author: "Mike Johnson",
    tags: ["SEO", "Google", "Marketing", "Analytics"],
  },
  {
    id: 4,
    title: "The Future of AI in Web Design",
    description:
      "How artificial intelligence is revolutionizing the way we create and optimize websites.",
    date: "July 15, 2025",
    image: "/assets/img/blog4.jpg",
    category: "AI & Design",
    readTime: "8 min read",
    author: "Sarah Wilson",
    tags: ["AI", "Design", "Automation", "UX"],
  },
  {
    id: 5,
    title: "Building Scalable React Applications",
    description:
      "Best practices and architectural patterns for creating maintainable React apps.",
    date: "July 5, 2025",
    image: "/assets/img/blog5.jpg",
    category: "React",
    readTime: "10 min read",
    author: "David Chen",
    tags: ["React", "Architecture", "Performance", "Scalability"],
  },
  {
    id: 6,
    title: "Cybersecurity Best Practices for 2025",
    description:
      "Essential security measures every developer and business should implement.",
    date: "June 25, 2025",
    image: "/assets/img/blog6.jpg",
    category: "Security",
    readTime: "9 min read",
    author: "Alex Rodriguez",
    tags: ["Security", "Privacy", "Data Protection", "Best Practices"],
  },
];

const categories = ["All", "Web Development", "Mobile Development", "SEO", "AI & Design", "React", "Security"];

const Blogs = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&family=JetBrains+Mono:wght@400;500&display=swap');
        
        .font-display {
          font-family: 'Inter', sans-serif;
          font-weight: 900;
        }
        
        .font-mono-body {
          font-family: 'JetBrains Mono', monospace;
          font-weight: 400;
        }
        
        .font-heading {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
        }

        .blog-card {
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          position: relative;
          overflow: hidden;
        }

        .blog-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(139, 0, 0, 0.05), transparent);
          transition: left 0.6s;
        }

        .blog-card:hover::before {
          left: 100%;
        }

        .blog-card:hover {
          transform: translateY(-12px);
          box-shadow: 0 25px 50px rgba(139, 0, 0, 0.2);
        }

        .blog-card:hover .blog-image {
          transform: scale(1.1);
        }

        .blog-image {
          transition: transform 0.6s ease;
        }

        .search-input {
          transition: all 0.3s ease;
        }

        .search-input:focus {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(139, 0, 0, 0.1);
        }

        .category-btn {
          transition: all 0.3s ease;
        }

        .category-btn:hover {
          transform: translateY(-2px);
        }

        .newsletter-input {
          transition: all 0.3s ease;
        }

        .newsletter-input:focus {
          transform: translateY(-2px);
        }

        .cta-btn {
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .cta-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s;
        }

        .cta-btn:hover::before {
          left: 100%;
        }
      `}</style>

      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900 py-24 overflow-hidden">
        {/* Decorative Wave Patterns */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <svg className="absolute top-0 left-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="wave-pattern-blogs" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <path d="M0 50 Q 25 25, 50 50 T 100 50" stroke="#8b0000" strokeWidth="0.5" fill="none" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#wave-pattern-blogs)" />
          </svg>
        </div>

        {/* Gradient Orbs */}
        <div className="absolute top-20 right-20 w-96 h-96 bg-red-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-red-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          {/* Badge */}
          <div className="inline-block mb-6">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-red-50 text-red-700 text-sm font-heading border border-red-200">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2 5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 002 2H4a2 2 0 01-2-2V5zm3 1h6v4H5V6zm6 6H5v2h6v-2z" clipRule="evenodd"/>
                <path d="M15 7h1a2 2 0 012 2v5.5a1.5 1.5 0 01-3 0V7z"/>
              </svg>
              Insights & Articles
            </span>
          </div>

          {/* <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display mb-6 leading-tight">
            Latest Blogs
          </h1> */}

          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display text-gray-900 mb-6 leading-tight">
                Latest {" "}
                <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                  Blogs
                </span>
          </h1>

          <div className="w-32 h-1.5 bg-gradient-to-r from-red-600 to-red-800 mx-auto mb-8 rounded-full"></div>
          
          <p className="text-lg sm:text-xl mb-12 max-w-2xl mx-auto font-mono-body leading-relaxed text-gray-600">
            Stay updated with trends, insights, and practical advice from our experts.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search articles..."
                className="search-input w-full px-6 py-4 pl-14 rounded-xl text-gray-900 bg-white border-2 border-gray-200 focus:outline-none focus:border-red-700 shadow-lg"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                <svg className="h-6 w-6 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-10 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`category-btn px-6 py-3 rounded-lg text-sm font-heading transition-all duration-200 ${
                  selectedCategory === category
                    ? "bg-[#8b0000] text-white shadow-lg"
                    : "bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          <p className="text-center text-gray-600 font-mono-body">
            {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
        {/* Background Orbs */}
        <div className="absolute top-40 right-40 w-80 h-80 bg-red-100 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-40 left-40 w-80 h-80 bg-red-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{ animationDelay: '3s' }}></div>

        <div className="container mx-auto px-4 relative z-10">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl shadow-lg border border-gray-100">
              <div className="text-gray-300 text-7xl mb-6">🔍</div>
              <h3 className="text-3xl font-display text-gray-900 mb-3">No articles found</h3>
              <p className="text-gray-500 font-mono-body">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="blog-card bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 backdrop-blur-sm bg-opacity-90"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="blog-image w-full h-56 object-cover"
                      onError={(e) => {
                        e.target.src = `https://via.placeholder.com/400x300/8b0000/ffffff?text=${encodeURIComponent(post.title)}`;
                      }}
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1.5 bg-gradient-to-r from-red-600 to-red-700 text-white text-xs font-heading rounded-full shadow-lg">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4 font-mono-body">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(post.date)}
                      </span>
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {post.readTime}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-heading text-gray-900 mb-3 line-clamp-2 hover:text-red-700 transition-colors cursor-pointer">
                      {post.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-6 line-clamp-3 font-mono-body text-sm leading-relaxed">
                      {post.description}
                    </p>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-red-700 rounded-full flex items-center justify-center text-white text-sm font-heading shadow-md">
                          {post.author.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className="ml-3 text-sm text-gray-700 font-heading">
                          {post.author}
                        </span>
                      </div>
                      
                      <button className="text-red-700 hover:text-red-900 font-heading text-sm transition-colors flex items-center">
                        Read More
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
                      {post.tags.slice(0, 3).map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="px-2.5 py-1 bg-red-50 text-red-700 text-xs rounded-md font-mono-body border border-red-100"
                        >
                          #{tag}
                        </span>
                      ))}
                      {post.tags.length > 3 && (
                        <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-md font-mono-body">
                          +{post.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute top-0 left-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="newsletter-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <path d="M0 50 Q 25 25, 50 50 T 100 50" stroke="#ffffff" strokeWidth="0.5" fill="none" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#newsletter-pattern)" />
          </svg>
        </div>

        <div className="absolute top-20 right-20 w-96 h-96 bg-red-600 rounded-full mix-blend-overlay filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-red-700 rounded-full mix-blend-overlay filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="inline-block mb-6">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-white bg-opacity-20 text-white text-sm font-heading border border-white border-opacity-30">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
              </svg>
              Newsletter
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl font-display mb-4">Stay Updated</h2>
          <p className="text-lg sm:text-xl mb-10 max-w-2xl mx-auto font-mono-body opacity-90 leading-relaxed">
            Get the latest articles and insights delivered directly to your inbox.
          </p>
          
          <div className="max-w-lg mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 bg-white bg-opacity-10 backdrop-blur-sm p-2 rounded-xl border border-white border-opacity-20">
              <input
                type="email"
                placeholder="Enter your email"
                className="newsletter-input flex-1 px-6 py-4 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-red-700 font-mono-body"
              />
              <button className="cta-btn px-8 py-4 bg-[#8b0000] hover:bg-[#6b0000] text-white font-heading rounded-lg transition-colors shadow-lg whitespace-nowrap">
                Subscribe
              </button>
            </div>
            <p className="text-sm text-gray-300 mt-4 font-mono-body">
              Join 10,000+ subscribers • No spam, unsubscribe anytime
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Blogs;